import { useState, useEffect } from 'react';
import { HowItWorks, Loading, api, useToast } from './_barrel.js';


        function SubjectsView({ token, setView, setSelectedSubject, openDialog }) {
            const [data, setData] = useState(null);
            const [error, setError] = useState(null);
            const [refresh, setRefresh] = useState(0);
            const [showArchived, setShowArchived] = useState(false);
            const [signing, setSigning] = useState(false);
            const toast = useToast();

            useEffect(() => {
                (async () => {
                    try {
                        const qs = showArchived ? '&include_archived=true' : '';
                        setData(await api(`/api/v1/subjects?limit=200${qs}`, {}, token));
                    } catch (e) { setError(e.message); }
                })();
            }, [token, refresh, showArchived]);

            const quickArchive = async (e, subject) => {
                // Stop the row's onClick from firing
                e.stopPropagation();
                const reason = prompt(`Archive "${subject.display_name || subject.full_name}"? Enter a brief reason:`, "");
                if (!reason || !reason.trim()) return;
                try {
                    await api(`/api/v1/subjects/${subject.id}`, {
                        method: 'DELETE',
                        body: JSON.stringify({ deletion_reason: reason.trim() }),
                    }, token);
                    toast(`Archived ${subject.display_name || subject.full_name}`);
                    setRefresh(r => r+1);
                } catch (err) { setError(err.message); }
            };

            const quickRestore = async (e, subject) => {
                e.stopPropagation();
                if (!confirm(`Restore "${subject.display_name || subject.full_name}" from archive?`)) return;
                try {
                    await api(`/api/v1/subjects/${subject.id}/restore`, { method: 'POST' }, token);
                    toast(`Restored ${subject.display_name || subject.full_name}`);
                    setRefresh(r => r+1);
                } catch (err) { setError(err.message); }
            };

            // Volume tier: sign every routine (Low/Moderate) unsigned assessment
            // in one pass. High/Critical are returned as exceptions for individual
            // senior review, never bulk-signed.
            const portfolioSign = async () => {
                const ids = (data?.items || []).filter(s => !s.deleted_at && !s.is_signed).map(s => s.id);
                if (ids.length === 0) { toast('No unsigned assessments to sign.'); return; }
                setSigning(true);
                try {
                    const r = await api('/api/v1/assessments/rubric/portfolio-sign',
                        { method: 'POST', body: JSON.stringify({ subject_ids: ids }) }, token);
                    toast(`Portfolio sign-off — ${r.signed} signed · ${r.exceptions} need individual review · ${r.skipped} skipped`);
                    setRefresh(x => x + 1);
                } catch (e) {
                    toast('Portfolio sign failed: ' + ((e.data && e.data.detail) || e.message));
                } finally { setSigning(false); }
            };

            const unsignedCount = (data?.items || []).filter(s => !s.deleted_at && !s.is_signed).length;

            return (
                <div className="max-w-7xl mx-auto px-6 py-10">
                    <div className="flex justify-between items-end mb-8">
                        <div>
                            <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">Roster</div>
                            <h2 className="font-display text-4xl gold">Subjects</h2>
                        </div>
                        <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-xs font-mono text-muted cursor-pointer">
                                <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
                                Show archived
                            </label>
                            {unsignedCount > 0 && (
                                <button onClick={portfolioSign} disabled={signing}
                                    title="Sign off all routine (Low/Moderate) unsigned assessments at once. High and Critical bands are flagged for individual review."
                                    style={{ padding: '8px 14px', fontSize: 13, border: '1px solid var(--veil)',
                                             borderRadius: 6, background: 'var(--zone-2)', color: 'var(--text)',
                                             cursor: signing ? 'wait' : 'pointer' }}>
                                    {signing ? 'Signing…' : `Portfolio sign-off (${unsignedCount})`}
                                </button>
                            )}
                            <button className="btn-gold" onClick={() => openDialog('new-subject', { onSuccess: () => setRefresh(r => r+1) })}>+ Add Subject</button>
                        </div>
                    </div>

                    {error && <div className="text-[var(--red)] mb-4">{error}</div>}
                    <HowItWorks id="subjects" title="How the Subjects desk works"
                        intro={'Subjects are the people you protect or investigate. You supply the person and a <b>lawful basis</b>; the platform sweeps open sources, breach data and adverse media, then scores them on the shared risk scale for you to review and sign.'}
                        steps={[
                            '<b>+ Add Subject</b> — name, subject type and lawful basis, plus optional <b>Also known as</b> names and <b>Online handles</b> (@name, platform:name or a profile URL) so the first sweep searches those too instead of a bare name. The lawful basis is your GDPR/DPA record of <i>why</i> you are monitoring them.',
                            'Open the record — it pulls <b>online mentions, watched accounts, breach exposure, sanctions and adverse media</b> and drafts a risk assessment.',
                            '<b>Generate / refresh the assessment</b> — one click re-runs every source and re-scores. The result stays on screen until you generate a new one.',
                            '<b>Review</b> — read the narrative and check every claim\'s <b>source chips</b>: each carries a source-reliability grade (Admiralty <b>A–F</b>, colour-coded). Where your judgement differs, open the incident and <b>override the grade</b>. The AI suggests; the analyst signs.',
                            '<b>Sign off</b> to lock the assessment as a defensible record, then export the Brief or printable one-pager — citations and reliability grades carry into the signed PDF\'s source index.',
                            '<b>Archive</b> (with a reason) when monitoring ends — the record is retained for audit and its GDPR <b>retention clock</b> starts. <b>Restore</b> to resume monitoring.',
                            '<b>Export data (DSAR)</b> downloads everything held on a subject as JSON for a subject-access request. <b>Erase (GDPR)</b> (admin, typed confirmation) permanently deletes the subject and all associated records — irreversible, unlike Archive.',
                        ]} />
                    {!data ? <Loading /> : data.items.length === 0 ? (
                        <div className="panel p-12 text-center text-muted">No subjects yet. Add your first one to begin.</div>
                    ) : (
                        <div className="panel">
                            <div className="px-6 py-3 border-b border-veil grid grid-cols-12 gap-4 text-xs font-mono uppercase tracking-widest text-muted">
                                <div className="col-span-4">Name</div>
                                <div className="col-span-2">Type</div>
                                <div className="col-span-2">Lawful Basis</div>
                                <div className="col-span-2">Status</div>
                                <div className="col-span-1 text-right">Created</div>
                                <div className="col-span-1 text-right"></div>
                            </div>
                            {data.items.map(s => (
                                <div key={s.id} className="table-row px-6 py-4 grid grid-cols-12 gap-4 items-center cursor-pointer"
                                    onClick={() => { setSelectedSubject(s.id); setView('subject-detail'); }}>
                                    <div className="col-span-4">
                                        <div className="font-display text-lg">{s.display_name || s.full_name}</div>
                                        {s.display_name && <div className="text-xs text-muted font-mono">{s.full_name}</div>}
                                        {s.client_tag && (
                                            <div className="text-xs font-mono mt-1" style={{color:'var(--gold)'}}>
                                                {s.client_tag}
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-span-2 text-sm font-mono text-muted">{s.subject_type_code}</div>
                                    <div className="col-span-2 text-sm font-mono text-muted">{s.lawful_basis.replace('_',' ')}</div>
                                    <div className="col-span-2">
                                        {s.deleted_at ? <span className="label-tag" style={{color:'var(--text-dim)'}}>Archived</span> :
                                         s.is_minor ? <span className="label-tag" style={{color:'var(--amber)'}}>Minor · Restricted</span> :
                                         <span className="label-tag" style={{color:'var(--green)'}}>Active</span>}
                                    </div>
                                    <div className="col-span-1 text-right text-xs font-mono text-dim">
                                        {new Date(s.created_at).toLocaleDateString('en-GB')}
                                    </div>
                                    <div className="col-span-1 text-right">
                                        {s.deleted_at ? (
                                            <button className="text-xs font-mono uppercase tracking-widest text-muted hover:text-[var(--gold)]"
                                                onClick={(e) => quickRestore(e, s)}>
                                                Restore
                                            </button>
                                        ) : (
                                            <button className="text-xs font-mono uppercase tracking-widest text-muted hover:text-[var(--red)]"
                                                onClick={(e) => quickArchive(e, s)}>
                                                Archive
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

export { SubjectsView };
